import { Box, Button, CircularProgress, Divider, Grid2, Typography } from '@mui/material'
import PageContainer from 'components/PageContainer/PageContainer'
import Stepper from 'components/Stepper/Stepper'
import React, { lazy, Suspense, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { useContractStore } from 'context/ContractStore'
import ActionModal from 'asterX-UI/Modals/ActionModal'
import { FileXIcon } from 'asterX-UI/Icons/FileXIcon'
import { GRAY_COLORS } from 'asterX-UI'
import { CreditFlow } from 'lib/api/services/creditFlow.service'
import { useResErrors } from 'lib/hooks'
import { enqueueSnackbar } from 'notistack'
import { checkCreditProduct, normalizePhone } from 'lib/utils'
import { useAuthStore } from 'context/AuthStore'
import { clientDataFields, collateralDataFields, loanApplicationDataFields } from './new-application'

const ClientData = lazy(() => import('./components/ClientData/ClientData'))
const ClientDocuments = lazy(() => import('./components/ClientDocuments/ClientDocuments'))
const LoanApplicationData = lazy(() => import('./components/LoanApplicationData/LoanApplicationData'))
const PledgeData = lazy(() => import('./components/PledgeData/PledgeData'))

const getStepperList = creditProductName => {
  const steps = [
    { name: 'Клиент', component: ClientData },
    { name: 'Заявка на заем', component: LoanApplicationData },
    { name: 'Залог', component: PledgeData },
    { name: 'Документы клиента', component: ClientDocuments }
  ]

  return checkCreditProduct('CL01', creditProductName) ? steps.filter(({ name }) => name !== 'Залог') : steps
}

const requiredClientDocs = [
  'CLIENT_IDENTITY_DOCUMENT',
  'DATA_PROCESSING_CONSENT',
  'CLIENT_PHOTO',
  'SIGNED_QUESTIONNAIRE'
]

const NewApplication = () => {
  const { standardCheck } = useResErrors()
  const navigate = useNavigate()
  const location = useLocation()
  const id = location.pathname.split('/').pop()
  const { authStoreFunc } = useAuthStore()
  const {
    contractStore: { newClient, newLoanApplication, newCollaterals, loanApplicationData, ClientDocs },
    contractStoreFunc: {
      getApplicationById,
      getBsData,
      validateFields,
      setNewCollaterals,
      getFileCategories,
      setClientDocs
    }
  } = useContractStore()

  const [openModal, setOpenModal] = useState(false)
  const [step, setStep] = useState(() => {
    const saved = sessionStorage.getItem('newApplicationStep')
    return saved ? Number(saved) : 0
  })

  const updateStep = newStep => {
    setStep(newStep)
    sessionStorage.setItem('newApplicationStep', newStep)
  }

  const stepperList = getStepperList(newLoanApplication?.creditProduct?.name)
  const StepComponent = stepperList[step].component

  const getInitData = async () => {
    await getApplicationById(id)
    await getBsData()
    await getFileCategories(id)
  }

  useEffect(() => {
    if (id) getInitData()
  }, [id])

  useEffect(() => {
    if (loanApplicationData.collaterals?.length > 0 && newCollaterals.length === 0) {
      setNewCollaterals(loanApplicationData.collaterals)
    }
  }, [loanApplicationData.collaterals])

  const goToNextStep = () => updateStep(step + 1)
  const goToPreviousStep = () => step > 0 && updateStep(step - 1)

  const handleCompleteCreation = async () => {
    if (!newLoanApplication?.id) return enqueueSnackbar('Ошибка', { variant: 'error' })
    if (!newCollaterals?.length) return enqueueSnackbar('Нет залогов для отправки', { variant: 'warning' })

    try {
      authStoreFunc.setLoading(true)

      const result = await CreditFlow.completeCreation({ loanApplicationId: newLoanApplication.id })
      if (!result) return enqueueSnackbar('Ошибка при создании заявки', { variant: 'error' })

      navigate(`/application/${newLoanApplication.id}`)
      sessionStorage.removeItem('newApplicationStep')
    } catch (err) {
      console.error(err)
      enqueueSnackbar('Произошла непредвиденная ошибка', { variant: 'error' })
    } finally {
      authStoreFunc.setLoading(false)
    }
  }

  const handleSaveClient = async () => {
    if (!newClient?.id) return enqueueSnackbar('Ошибка', { variant: 'error' })
    const data = {
      ...newClient,
      mobilePhone1: normalizePhone(newClient.mobilePhone1),
      country: { name: newClient.country.name, bsId: newClient.country.countryId },
      documentType: { name: newClient.documentType.name, bsId: newClient.documentType.complementaryDocTypeId },
      education: { name: newClient.education.name, bsId: newClient.education.educationId },
      maritalStatus: { name: newClient.maritalStatus.name, bsId: newClient.maritalStatus.maritalStatusId },
      sexName: { name: newClient.sexName.name, bsId: newClient.sexName.sexId }
    }
    const res = await CreditFlow.changeClientById(data)
    standardCheck(res, () => {
      enqueueSnackbar('Успешно', { variant: 'info' })
      getApplicationById(id)
      goToNextStep()
    })
  }

  const handleSaveLoanApplication = async isCreditCL01 => {
    if (!newLoanApplication?.id) return enqueueSnackbar('Ошибка', { variant: 'error' })
    const data = {
      ...newLoanApplication,
      creditProduct: {
        name: newLoanApplication.creditProduct.name,
        bsId: newLoanApplication.creditProduct.creditProductId
      },
      currency: { name: newLoanApplication.currency.name, bsId: newLoanApplication.currency.currencyId },
      loanPurpose: { name: newLoanApplication.loanPurpose.name, bsId: newLoanApplication.loanPurpose.creditPurposeId }
    }
    const res = await CreditFlow.changeApplicationById(newLoanApplication.id, data)
    standardCheck(res, async () => {
      enqueueSnackbar('Успешно', { variant: 'info' })
      if (!isCreditCL01) await getApplicationById(id)
    })
  }

  const handleDeleteApplication = async () => {
    if (!newLoanApplication?.id) return enqueueSnackbar('Ошибка', { variant: 'error' })
    const res = await CreditFlow.deleteApplicationById(newLoanApplication.id)
    standardCheck(res, () => {
      enqueueSnackbar('Заявка удалена', { variant: 'info' })
      sessionStorage.removeItem('newApplicationStep')
      setOpenModal(false)
      setStep(0)
      navigate('/home')
    })
  }

  const handleCancelApplication = () => {
    setOpenModal(false)
    navigate('/home')
    sessionStorage.removeItem('newApplicationStep')
  }

  const isClientDocs = loanApplicationData?.files?.filter(f => f.category.step === 'userCreateLoanApplication') || []
  const isClientDocsValid = requiredClientDocs.every(code => isClientDocs.some(f => f.category.code === code))

  const handleNextStep = async () => {
    const creditName = newLoanApplication?.creditProduct?.name
    const isCreditCL01 = checkCreditProduct('CL01', creditName)
    const lastIndex = stepperList.length - 1

    const isFirstStep = step === 0
    const isLoanStep = step === 1
    const isPledgeStep = step === 2
    const isDocsStep = step === lastIndex
    const isLastStep = isCreditCL01 ? isLoanStep : isDocsStep

    if (isFirstStep) {
      if (!validateFields(clientDataFields, newClient)) {
        return enqueueSnackbar('Пожалуйста, заполните все обязательные поля', { variant: 'info' })
      }
      await handleSaveClient()
      return
    }

    if (isLoanStep) {
      const valid = validateFields(loanApplicationDataFields(isCreditCL01), newLoanApplication)
      if (!valid) {
        return enqueueSnackbar('Пожалуйста, заполните все обязательные поля', { variant: 'info' })
      }
      await handleSaveLoanApplication(isCreditCL01)

      if (isCreditCL01) {
        const result = await CreditFlow.completeCreation({ loanApplicationId: newLoanApplication.id })
        standardCheck(result, () => {
          if (result) {
            navigate(`/application/${newLoanApplication.id}`)
            sessionStorage.removeItem('newApplicationStep')
          }
        })
      } else {
        enqueueSnackbar('Успешно', { variant: 'info' })
        goToNextStep()
      }
      return
    }

    if (!isCreditCL01 && isPledgeStep) {
      let ok = true
      newCollaterals.forEach(coll => {
        const guarantee =
          coll.type === 'CLIENT' ? coll.clientGuarantee : coll.type === 'LEGAL_CLIENT' ? coll.legalGuarantee : {}
        if (!validateFields(collateralDataFields(coll.type), { ...coll, ...guarantee })) ok = false
      })
      if (!ok) {
        return enqueueSnackbar('Пожалуйста, заполните все поля по залогу', { variant: 'info' })
      }

      const toCreate = []
      const toUpdate = []

      newCollaterals.forEach(coll => {
        const groupName = typeof coll.group === 'string' ? coll.group : coll.group?.name || ''
        const dealershipName = typeof coll.dealership === 'string' ? coll.dealership : coll.dealership?.name || ''

        const payload = {
          ...coll,
          loanApplicationId: newLoanApplication.id,
          group: groupName,
          dealership: dealershipName
        }

        if (coll.id) {
          toUpdate.push({ id: coll.id, data: payload })
        } else {
          toCreate.push(payload)
        }
      })

      try {
        await Promise.all(toUpdate.map(({ id, data }) => CreditFlow.updateCollateral(id, data)))

        if (toCreate.length) {
          await CreditFlow.createCollateral({ collaterals: toCreate })
        }

        enqueueSnackbar('Залоги успешно сохранены', { variant: 'success' })
        goToNextStep()
      } catch (err) {
        console.error(err)
        enqueueSnackbar('Ошибка при сохранении залогов', { variant: 'error' })
      }

      return
    }

    if (isLastStep) {
      if (!isClientDocsValid) {
        return enqueueSnackbar('Пожалуйста, загрузите все обязательные документы', { variant: 'info' })
      }
      await handleCompleteCreation()
    }
  }

  return (
    <PageContainer>
      <Grid2 xs={12} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="subtitle1">Новая кредитная заявка</Typography>
        <Button size="small" onClick={() => setOpenModal(true)}>
          Отменить
        </Button>
      </Grid2>

      <Grid2 xs={12}>
        <Stepper steps={stepperList} currentStep={step} />
      </Grid2>
      <Divider sx={{ my: 2 }} />

      <Suspense
        fallback={
          <Box display="flex" justifyContent="center">
            <CircularProgress />
          </Box>
        }
      >
        <Box display="flex" flexDirection="column" gap={4}>
          {stepperList[step].name === 'Залог' && <PledgeData data={newCollaterals} setData={setNewCollaterals} />}

          {stepperList[step].name === 'Документы клиента' && (
            <ClientDocuments data={ClientDocs} setData={setClientDocs} />
          )}

          {stepperList[step].name !== 'Залог' && stepperList[step].name !== 'Документы клиента' && (
            <StepComponent
            // ClientData и LoanApplicationData внутри сами берут свой кусок из контекста
            />
          )}
        </Box>
      </Suspense>

      <Grid2 container sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 'auto' }}>
        <Grid2>
          {step === 0 ? (
            <Button variant="outlined" color="gray" onClick={() => setOpenModal(true)}>
              Отменить
            </Button>
          ) : (
            <Button variant="outlined" color="gray" onClick={goToPreviousStep}>
              Назад
            </Button>
          )}
        </Grid2>
        <Grid2>
          <Button variant="contained" color="primary" onClick={handleNextStep}>
            {step === stepperList.length - 1 ? 'Завершить' : 'Далее'}
          </Button>
        </Grid2>
      </Grid2>

      <ActionModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        onCancel={handleDeleteApplication}
        onConfirm={handleCancelApplication}
        labelCancel="Удалить"
        labelConfirm="Продолжить позже"
        cancelBtnRed
        confirmVariant="outlined"
        color="black"
        borderColor={GRAY_COLORS[300]}
      >
        <Grid2 xs={12} display="flex" flexDirection="column" alignItems="center" justifyContent="center">
          <Grid2
            display="flex"
            alignItems="center"
            sx={{
              border: `1px solid
          ${GRAY_COLORS[200]}`,
              p: 2,
              borderRadius: 1,
              mb: 2
            }}
          >
            <FileXIcon />
          </Grid2>
          <Typography variant="text" sx={{ fontSize: 18, fontWeight: 600 }}>
            Отменить заполнение заявки?
          </Typography>
          <Typography sx={{ fontSize: 14, color: GRAY_COLORS[600] }}>Это действие нельзя будет отменить</Typography>
        </Grid2>
      </ActionModal>
    </PageContainer>
  )
}

export default NewApplication
